/**
 * PickupSystem - Handles spawning and collection of food pickups
 * 
 * Spawns pickups on floor tiles throughout the maze based on configured
 * spawn weights and manages player collection.
 */

import Phaser from 'phaser';
import { Pickup } from '../entities/Pickup';
import { Player } from '../entities/Player';
import { FartInventory } from '../entities/farts/FartInventory';
import { PickupType, getPickupSpawnProbabilities, getPickupConfig, getFartTypeFromPickup } from '../entities/farts/FartTypes';
import { TileType } from './MazeGenerator';
import { TILE_SIZE } from '../config/game.config';
import { networkManager } from '../network/NetworkManager';

export interface PickupSystemConfig {
    /** Minimum pickups to spawn */
    minPickups?: number;
    /** Maximum pickups to spawn */
    maxPickups?: number;
    /** Minimum distance from spawn points (tiles) */
    minSpawnDistance?: number;
    /** Respawn interval in ms (0 = no respawn) */
    respawnInterval?: number;
}

const DEFAULT_CONFIG: Required<PickupSystemConfig> = {
    minPickups: 3,
    maxPickups: 6,
    minSpawnDistance: 3,
    respawnInterval: 30000, // 30 seconds
};

export class PickupSystem {
    private scene: Phaser.Scene;
    private pickups: Phaser.Physics.Arcade.Group;
    private player: Player;
    private inventory: FartInventory;
    private mazeData: TileType[][];
    private spawnPoints: { x: number; y: number }[];
    private config: Required<PickupSystemConfig>;

    private floorTiles: { x: number; y: number }[] = [];
    private respawnTimer?: Phaser.Time.TimerEvent;
    private isMultiplayer: boolean;
    private pickupIdCounter: number = 0;  // For generating unique IDs

    // Callback for when pickup is collected
    private onCollectCallback?: () => void;


    constructor(
        scene: Phaser.Scene,
        player: Player,
        inventory: FartInventory,
        mazeData: TileType[][],
        spawnPoints: { x: number; y: number }[],
        config?: PickupSystemConfig,
        isMultiplayer: boolean = false
    ) {
        this.scene = scene;
        this.player = player;
        this.inventory = inventory;
        this.mazeData = mazeData;
        this.spawnPoints = spawnPoints;
        this.config = { ...DEFAULT_CONFIG, ...config };
        this.isMultiplayer = isMultiplayer;

        // Create physics group for pickups
        this.pickups = scene.physics.add.group({
            classType: Pickup,
            runChildUpdate: false
        });

        // Cache floor tiles for spawning
        this.cacheFloorTiles();

        // Set up collection overlap
        this.setupCollectionOverlap();

        // Set up multiplayer sync
        if (this.isMultiplayer) {
            this.setupMultiplayerSync();
        }
    }

    /**
     * Set up multiplayer sync for pickup collection
     */
    private setupMultiplayerSync(): void {
        networkManager.subscribe('PICKUP_COLLECTED', (message) => {
            const { pickupId } = message.payload as {
                playerId: string;
                pickupId: string;
            };

            // Find and remove the pickup by ID
            this.removePickupById(pickupId);
        });
    }

    /**
     * Remove a pickup by its ID (for multiplayer sync)
     */
    private removePickupById(id: string): void {
        const pickups = this.pickups.getChildren() as Pickup[];
        const pickup = pickups.find(p => p.active && p.id === id);

        if (pickup) {
            console.log(`[PICKUP SYNC] Removing pickup ${id}`);
            pickup.collect(); // Play collection effect and destroy
        }
    }

    /**
     * Set callback for when player collects a pickup
     */
    onCollect(callback: () => void): void {
        this.onCollectCallback = callback;
    }

    /**
     * Cache all floor tiles that are valid spawn locations
     */
    private cacheFloorTiles(): void {
        for (let y = 0; y < this.mazeData.length; y++) {
            for (let x = 0; x < this.mazeData[y].length; x++) {
                if (this.mazeData[y][x] === TileType.FLOOR) {
                    const worldX = x * TILE_SIZE + TILE_SIZE / 2;
                    const worldY = y * TILE_SIZE + TILE_SIZE / 2;

                    // Check distance from spawn points
                    const tooCloseToSpawn = this.spawnPoints.some(spawn => {
                        const dist = Phaser.Math.Distance.Between(worldX, worldY, spawn.x, spawn.y);
                        return dist < this.config.minSpawnDistance * TILE_SIZE;
                    });

                    if (!tooCloseToSpawn) {
                        this.floorTiles.push({ x: worldX, y: worldY });
                    }
                }
            }
        }
    }

    /**
     * Set up collision detection for pickup collection
     */
    private setupCollectionOverlap(): void {
        this.scene.physics.add.overlap(
            this.player,
            this.pickups,
            (playerObj, pickupObj) => {
                const pickup = pickupObj as Pickup;
                this.collectPickup(pickup);
            }
        );
    }

    /**
     * Spawn initial pickups in the maze
     */
    spawnInitialPickups(seed?: number): void {
        const rng = seed !== undefined
            ? new Phaser.Math.RandomDataGenerator([seed.toString()])
            : new Phaser.Math.RandomDataGenerator();

        const pickupCount = rng.between(this.config.minPickups, this.config.maxPickups);

        // Shuffle floor tiles deterministically using seeded RNG
        // (Phaser.Utils.Array.Shuffle uses Math.random which is not seeded!)
        const shuffled = this.seededShuffle([...this.floorTiles], rng);
        const spawnLocations = shuffled.slice(0, Math.min(pickupCount, shuffled.length));

        // Get spawn probabilities
        const probabilities = getPickupSpawnProbabilities();
        const pickupTypes = Object.keys(probabilities) as PickupType[];

        console.log(`[PICKUPS] Spawning ${spawnLocations.length} pickups with seed ${seed}`);

        for (const location of spawnLocations) {
            // Weighted random selection of pickup type
            const type = this.weightedRandomPickup(pickupTypes, probabilities, rng);
            this.spawnPickup(location.x, location.y, type);
            console.log(`[PICKUPS] Spawned ${type} at (${location.x}, ${location.y})`);
        }

        // Set up respawn timer if configured
        if (this.config.respawnInterval > 0) {
            this.startRespawnTimer();
        }
    }

    /**
     * Fisher-Yates shuffle using seeded RNG
     */
    private seededShuffle<T>(array: T[], rng: Phaser.Math.RandomDataGenerator): T[] {
        const result = [...array];
        for (let i = result.length - 1; i > 0; i--) {
            const j = rng.between(0, i);
            [result[i], result[j]] = [result[j], result[i]];
        }
        return result;
    }


    /**
     * Select a random pickup type based on weights
     */
    private weightedRandomPickup(
        types: PickupType[],
        weights: Record<PickupType, number>,
        rng: Phaser.Math.RandomDataGenerator
    ): PickupType {
        const totalWeight = types.reduce((sum, type) => sum + weights[type], 0);
        let random = rng.frac() * totalWeight;

        for (const type of types) {
            random -= weights[type];
            if (random <= 0) {
                return type;
            }
        }

        return types[0]; // Fallback
    }

    /**
     * Spawn a single pickup at a location
     */
    spawnPickup(x: number, y: number, type: PickupType): Pickup {
        const id = `pickup_${this.pickupIdCounter++}`;

        const pickup = new Pickup(this.scene, {
            id,
            pickupType: type,
            x,
            y
        });

        this.pickups.add(pickup);
        return pickup;
    }

    /**
     * Handle collecting a pickup
     */
    private collectPickup(pickup: Pickup): void {
        // Prevent double collection if physics is already disabled
        if (!pickup.body || !pickup.body.enable) return;

        const fartType = pickup.getFartType();
        const ammoAmount = pickup.getAmmoAmount();

        if (fartType) {
            // Add ammo to inventory
            this.inventory.addAmmo(fartType, ammoAmount);

            // Auto-select the picked up fart type
            this.inventory.selectType(fartType);

            // Log for debugging
            console.log(`Collected ${pickup.pickupType}! +${ammoAmount} ${fartType} ammo (auto-selected)`);

            // Play collection effect
            pickup.collect();

            // Notify UI
            this.onCollectCallback?.();

            // Broadcast in multiplayer (pickup is consumed)
            if (this.isMultiplayer) {
                networkManager.broadcast('PICKUP_COLLECTED', {
                    playerId: networkManager.playerId,
                    pickupId: pickup.id
                });
            }
        }
    }

    /**
     * Start respawn timer
     */
    private startRespawnTimer(): void {
        this.respawnTimer = this.scene.time.addEvent({
            delay: this.config.respawnInterval,
            callback: this.respawnPickups,
            callbackScope: this,
            loop: true
        });
    }

    /**
     * Respawn pickups to maintain density
     */
    private respawnPickups(): void {
        const currentCount = this.pickups.countActive();
        const targetCount = Math.floor((this.config.minPickups + this.config.maxPickups) / 2);

        if (currentCount < targetCount) {
            const toSpawn = Math.min(2, targetCount - currentCount); // Spawn up to 2 at a time

            const rng = new Phaser.Math.RandomDataGenerator();
            const probabilities = getPickupSpawnProbabilities();
            const pickupTypes = Object.keys(probabilities) as PickupType[];

            // Find empty floor tiles
            const occupiedPositions = new Set<string>();
            (this.pickups.getChildren() as Pickup[]).forEach(p => {
                occupiedPositions.add(`${Math.round(p.x)},${Math.round(p.y)}`);
            });

            const availableTiles = this.floorTiles.filter(
                tile => !occupiedPositions.has(`${Math.round(tile.x)},${Math.round(tile.y)}`)
            );

            const shuffled = Phaser.Utils.Array.Shuffle([...availableTiles]);

            for (let i = 0; i < toSpawn && i < shuffled.length; i++) {
                const type = this.weightedRandomPickup(pickupTypes, probabilities, rng);
                this.spawnPickup(shuffled[i].x, shuffled[i].y, type);
            }
        }
    }

    /**
     * Get active pickup count
     */
    getPickupCount(): number {
        return this.pickups.countActive();
    }

    /**
     * Get all active pickups
     */
    getPickups(): Pickup[] {
        return this.pickups.getChildren() as Pickup[];
    }

    /**
     * Clean up resources
     */
    destroy(): void {
        this.respawnTimer?.destroy();
        this.pickups.clear(true, true);
    }
}
